// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FluentProvider } from "@fluentui/react-components";
import {
  getAppTheme,
  type ColorScheme,
  type ThemeName,
} from "../theme/themes";
import {
  isDarkModeEnabled,
  syncDarkModeEnabledMarker,
} from "../theme/darkModeFlag";
import type { ReactNode } from "react";

const STORAGE_KEY = "riddle-theme";
export const THEME_MODE_STORAGE_KEY = "riddle-theme-mode";

export type ThemeMode = "light" | "dark" | "system";

const DARK_QUERY = "(prefers-color-scheme: dark)";

export const lightCssVars: Record<ThemeName, Record<string, string>> = {
  crimson: {
    "--riddle-bg-top": "#fff8f9",
    "--riddle-bg-bottom": "#ffe4e9",
    "--riddle-radial-1": "rgba(220, 20, 60, 0.14)",
    "--riddle-radial-2": "rgba(215, 0, 64, 0.14)",
    "--riddle-accent-soft": "rgba(255, 240, 242, 0.88)",
    "--riddle-accent-surface": "rgba(255, 246, 248, 0.98)",
    "--riddle-accent-border": "rgba(193, 67, 82, 0.12)",
    "--riddle-accent-border-strong": "rgba(220, 20, 60, 0.28)",
    "--riddle-accent-hover-border": "rgba(202, 0, 57, 0.2)",
    "--riddle-accent-tint": "rgba(220, 20, 60, 0.14)",
    "--riddle-accent-title": "#7a0027",
    "--riddle-accent-copy": "#8a3147",
    "--riddle-accent-summary": "#a20032",
    "--riddle-accent-badge-fg": "#8f0a32",
    "--riddle-accent-badge-border": "rgba(220, 20, 60, 0.18)",
    "--riddle-accent-shadow": "rgba(180, 0, 50, 0.12)",
    "--riddle-surface-primary": "rgba(255, 255, 255, 0.96)",
    "--riddle-surface-secondary": "rgba(255, 255, 255, 0.82)",
    "--riddle-surface-elevated": "rgba(255, 255, 255, 0.74)",
    "--riddle-surface-inset": "rgba(255, 255, 255, 0.68)",
    "--riddle-fg": "#1a0008",
    "--riddle-paper": "#ffffff",
  },
  ocean: {
    "--riddle-bg-top": "#f8faff",
    "--riddle-bg-bottom": "#e9f0ff",
    "--riddle-radial-1": "rgba(61, 118, 255, 0.14)",
    "--riddle-radial-2": "rgba(33, 64, 145, 0.14)",
    "--riddle-accent-soft": "rgba(236, 242, 255, 0.92)",
    "--riddle-accent-surface": "rgba(244, 248, 255, 0.98)",
    "--riddle-accent-border": "rgba(56, 109, 224, 0.14)",
    "--riddle-accent-border-strong": "rgba(56, 109, 224, 0.30)",
    "--riddle-accent-hover-border": "rgba(33, 64, 145, 0.24)",
    "--riddle-accent-tint": "rgba(56, 109, 224, 0.14)",
    "--riddle-accent-title": "#17357a",
    "--riddle-accent-copy": "#36508f",
    "--riddle-accent-summary": "#214091",
    "--riddle-accent-badge-fg": "#214091",
    "--riddle-accent-badge-border": "rgba(56, 109, 224, 0.20)",
    "--riddle-accent-shadow": "rgba(19, 52, 132, 0.14)",
    "--riddle-surface-primary": "rgba(255, 255, 255, 0.96)",
    "--riddle-surface-secondary": "rgba(249, 252, 255, 0.88)",
    "--riddle-surface-elevated": "rgba(246, 250, 255, 0.80)",
    "--riddle-surface-inset": "rgba(255, 255, 255, 0.72)",
    "--riddle-fg": "#0f1730",
    "--riddle-paper": "#ffffff",
  },
};

export const darkCssVars: Record<ThemeName, Record<string, string>> = {
  crimson: {
    "--riddle-bg-top": "#1c1214",
    "--riddle-bg-bottom": "#2a1419",
    "--riddle-radial-1": "rgba(235, 98, 119, 0.12)",
    "--riddle-radial-2": "rgba(220, 20, 60, 0.10)",
    "--riddle-accent-soft": "rgba(84, 0, 27, 0.45)",
    "--riddle-accent-surface": "rgba(53, 0, 16, 0.60)",
    "--riddle-accent-border": "rgba(240, 133, 142, 0.18)",
    "--riddle-accent-border-strong": "rgba(240, 133, 142, 0.36)",
    "--riddle-accent-hover-border": "rgba(240, 133, 142, 0.30)",
    "--riddle-accent-tint": "rgba(235, 98, 119, 0.18)",
    "--riddle-accent-title": "#f7bcc3",
    "--riddle-accent-copy": "#f4a1a9",
    "--riddle-accent-summary": "#f0858e",
    "--riddle-accent-badge-fg": "#f4a1a9",
    "--riddle-accent-badge-border": "rgba(240, 133, 142, 0.30)",
    "--riddle-accent-shadow": "rgba(0, 0, 0, 0.40)",
    "--riddle-surface-primary": "rgba(41, 32, 34, 0.96)",
    "--riddle-surface-secondary": "rgba(41, 32, 34, 0.82)",
    "--riddle-surface-elevated": "rgba(46, 36, 38, 0.78)",
    "--riddle-surface-inset": "rgba(28, 20, 22, 0.70)",
    "--riddle-fg": "#fdeef0",
    "--riddle-paper": "#ffffff",
  },
  ocean: {
    "--riddle-bg-top": "#0d1220",
    "--riddle-bg-bottom": "#121a33",
    "--riddle-radial-1": "rgba(118, 152, 248, 0.12)",
    "--riddle-radial-2": "rgba(56, 109, 224, 0.10)",
    "--riddle-accent-soft": "rgba(22, 38, 90, 0.45)",
    "--riddle-accent-surface": "rgba(16, 27, 58, 0.60)",
    "--riddle-accent-border": "rgba(137, 166, 251, 0.18)",
    "--riddle-accent-border-strong": "rgba(137, 166, 251, 0.36)",
    "--riddle-accent-hover-border": "rgba(137, 166, 251, 0.30)",
    "--riddle-accent-tint": "rgba(118, 152, 248, 0.18)",
    "--riddle-accent-title": "#c2ceff",
    "--riddle-accent-copy": "#afc1fe",
    "--riddle-accent-summary": "#9cb3fd",
    "--riddle-accent-badge-fg": "#afc1fe",
    "--riddle-accent-badge-border": "rgba(137, 166, 251, 0.30)",
    "--riddle-accent-shadow": "rgba(0, 0, 0, 0.40)",
    "--riddle-surface-primary": "rgba(28, 34, 52, 0.96)",
    "--riddle-surface-secondary": "rgba(28, 34, 52, 0.82)",
    "--riddle-surface-elevated": "rgba(32, 39, 60, 0.78)",
    "--riddle-surface-inset": "rgba(16, 21, 36, 0.70)",
    "--riddle-fg": "#eef2ff",
    "--riddle-paper": "#ffffff",
  },
};

export function getCssVars(
  name: ThemeName,
  scheme: ColorScheme,
): Record<string, string> {
  return scheme === "dark" ? darkCssVars[name] : lightCssVars[name];
}

export function parseThemeMode(value: string | null): ThemeMode {
  return value === "dark" || value === "system" ? value : "light";
}

function systemPrefersDark(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia(DARK_QUERY).matches
  );
}

export function resolveColorScheme(
  mode: ThemeMode,
  prefersDark: boolean,
): ColorScheme {
  if (mode === "system") {
    return prefersDark ? "dark" : "light";
  }
  return mode;
}

interface AppThemeContextValue {
  themeName: ThemeName;
  setThemeName: (name: ThemeName) => void;
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  colorScheme: ColorScheme;
}

const AppThemeContext = createContext<AppThemeContextValue>({
  themeName: "crimson",
  setThemeName: () => undefined,
  themeMode: "light",
  setThemeMode: () => undefined,
  colorScheme: "light",
});

export function useAppTheme(): AppThemeContextValue {
  return useContext(AppThemeContext);
}

interface AppThemeProviderProps {
  children: ReactNode;
}

export function AppThemeProvider({ children }: AppThemeProviderProps) {
  const [themeName, setThemeNameState] = useState<ThemeName>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "ocean" ? "ocean" : "crimson";
  });

  function setThemeName(name: ThemeName) {
    localStorage.setItem(STORAGE_KEY, name);
    setThemeNameState(name);
  }

  const [themeMode, setThemeModeState] = useState<ThemeMode>(() =>
    parseThemeMode(localStorage.getItem(THEME_MODE_STORAGE_KEY)),
  );
  const [prefersDark, setPrefersDark] = useState<boolean>(systemPrefersDark);

  function setThemeMode(mode: ThemeMode) {
    localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    setThemeModeState(mode);
  }

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) =>
      setPrefersDark(event.matches);
    setPrefersDark(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const darkModeEnabled = isDarkModeEnabled();

  useEffect(() => {
    syncDarkModeEnabledMarker(darkModeEnabled);
  }, [darkModeEnabled]);

  const colorScheme = darkModeEnabled
    ? resolveColorScheme(themeMode, prefersDark)
    : "light";

  const lastLoggedScheme = useRef<ColorScheme | undefined>(undefined);
  useEffect(() => {
    if (!darkModeEnabled || lastLoggedScheme.current === colorScheme) {
      return;
    }
    lastLoggedScheme.current = colorScheme;
    console.info(`[theme] color scheme ${colorScheme}`);
  }, [colorScheme, darkModeEnabled]);

  useEffect(() => {
    const root = document.documentElement;
    const vars = getCssVars(themeName, colorScheme);
    for (const [property, value] of Object.entries(vars)) {
      root.style.setProperty(property, value);
    }
    root.dataset.theme = colorScheme;
    root.style.colorScheme = colorScheme;
  }, [themeName, colorScheme]);

  const contextValue = useMemo(
    () => ({ themeName, setThemeName, themeMode, setThemeMode, colorScheme }),
    [themeName, themeMode, colorScheme],
  );

  return (
    <AppThemeContext.Provider value={contextValue}>
      <FluentProvider theme={getAppTheme(themeName, colorScheme)}>
        {children}
      </FluentProvider>
    </AppThemeContext.Provider>
  );
}
